import * as React from "react";
import { ReactNode, useCallback, useEffect, useRef } from "react";
import { isNil } from "ramda";

import { Grid } from "affinityUI/elements/grid";
import { Field, FieldLabel, ValidationError } from "affinityUI/form/components";
import { Input } from "affinityUI/form/inputs/input";

import { useAffinityField } from "../../helpers";

export const ConfirmField: React.FC<
  React.PropsWithChildren<
    {
      name: string;
      plain?: boolean;
      /** Function to change value before passing to Formik */
      parseValue?: (value?: Maybe<string>) => undefined | Maybe<string>;
      confirmLabel?: ReactNode;
    } & Partial<Pick<React.ComponentProps<typeof Input>, "type" | "disabled">>
  >
> = ({ plain, parseValue, confirmLabel, children, name, type, disabled }) => {
  const confirmInput = useRef<HTMLInputElement>(null);

  const [{ value }, meta, { setValue, setError }] = useAffinityField({
    name,
    validate: (value) =>
      !isNil(value) && value !== confirmInput.current?.value ? "Entered values do not match" : undefined,
  });

  useEffect(() => {
    if (!isNil(confirmInput.current)) {
      confirmInput.current.value = value ?? "";
    }
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [meta.initialValue]);

  const handleOnChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setError(undefined);
      if (!isNil(parseValue)) {
        setValue(parseValue(e.target.value));
      } else {
        setValue(e.target.value);
      }
    },
    [setValue, parseValue]
  );

  const handleOnConfirmChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setError(undefined);
      if (!isNil(parseValue)) {
        e.target.value = parseValue(e.target.value) ?? "";
      }
    },
    [setValue, parseValue]
  );

  const cols = plain ? [1] : { xs: [1], lg: [1, 1] };

  return (
    <Field role="group" aria-labelledby={name}>
      <Grid $cols={cols}>
        <div>
          <FieldLabel id={`${name}-label`} htmlFor={name}>
            {children}
          </FieldLabel>
          <Input
            value={value ?? ""}
            id={`${name}`}
            name={`${name}`}
            onChange={handleOnChange}
            $error={meta.error}
            data-testid={`${name}.value`}
            type={type}
            disabled={disabled}
          />
        </div>
        <div>
          <FieldLabel htmlFor={`${name}.confirm`}>{confirmLabel}</FieldLabel>
          <Input
            ref={confirmInput}
            defaultValue={value ?? ""}
            id={`${name}.confirm`}
            name={`${name}.confirm`}
            onChange={handleOnConfirmChange}
            $error={meta.error}
            data-testid={`${name}.confirm`}
            type={type}
            disabled={disabled}
          />
        </div>
      </Grid>
      {meta?.error && <ValidationError>{meta?.error}</ValidationError>}
    </Field>
  );
};
